import { getPaymentConfigKey } from "@/helpers/getPaymentConfigKey.ts";
import { RateGroup } from "@/helpers/types/types.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

/**
 * Returns a map of ratePlanId → isCompatible for all rate plans in the given rate group.
 * A rate plan is incompatible if its paymentConfig conflicts with items already in the cart.
 */
export const useCheckPaymentConfigCompatibility = (rateGroup: RateGroup): Record<string, boolean> => {
  const { cartAccommodationItems } = useEngineStore(
    useShallow((state) => ({
      cartAccommodationItems: state.cartAccommodationItems,
    })),
  );

  return useMemo(() => {
    const result: Record<string, boolean> = {};

    const cartConfigKeys = cartAccommodationItems
      .map((item) => item.ratePlan.paymentConfig)
      .filter((config) => !!config)
      .map((config) => getPaymentConfigKey(config));

    const uniqueCartKeys = [...new Set(cartConfigKeys)];

    for (const rp of rateGroup.ratePlans) {
      if (uniqueCartKeys.length === 0) {
        result[rp.id] = true;
        continue;
      }

      if (!rp.paymentConfig) {
        result[rp.id] = true;
        continue;
      }

      const candidateKey = getPaymentConfigKey(rp.paymentConfig);
      result[rp.id] = uniqueCartKeys.every((key) => key === candidateKey);
    }

    return result;
  }, [cartAccommodationItems, rateGroup.ratePlans]);
};
